import * as React from 'react';
import { Body, Link } from 'vet-bones/design-system/components';

import { DataTable, DataTableColumn } from 'src/ui/components/DataTable';
import {
  SduEndpointTableRow,
  formatTotalUnits,
} from 'src/ui/components/sdu-dashboard/aggregations';
import {
  SDU_DASHBOARD_ENDPOINT_TABLE_COLUMN_SORT,
  SDU_DASHBOARD_ENDPOINT_TABLE_ROW,
  SDU_DASHBOARD_ENDPOINT_TABLE_ROW_LAST_SYNCED,
  SDU_DASHBOARD_ENDPOINT_TABLE_ROW_UNITS,
  SDU_DASHBOARD_ENDPOINT_TABLE_SEARCH,
} from 'src/ui/constants/testIds';
import * as copy from 'src/ui/templates/copy';
import { formatLastSyncedTimestamp } from 'src/ui/utils/dateUtils';

interface SduEndpointConsumptionTableProps {
  onRowClick?: (row: SduEndpointTableRow) => void;
  rows: SduEndpointTableRow[];
}

const formatLastSynced = (iso: string | null): string => {
  if (!iso) {
    return copy.components.sduDashboard.noData;
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return copy.components.sduDashboard.noData;
  }
  return formatLastSyncedTimestamp(parsed);
};

const COLUMNS: Array<DataTableColumn<SduEndpointTableRow>> = [
  {
    key: 'url',
    header: copy.components.sduDashboard.endpointTableColumns.url,
    sortByAlpha: (item) => item.url,
    sortTestId: SDU_DASHBOARD_ENDPOINT_TABLE_COLUMN_SORT('url'),
  },
  {
    key: 'units',
    header: copy.components.sduDashboard.endpointTableColumns.units,
    sortByNumeric: (item) => item.totalUnits ?? Number.NEGATIVE_INFINITY,
    sortTestId: SDU_DASHBOARD_ENDPOINT_TABLE_COLUMN_SORT('units'),
  },
  {
    key: 'lastSynced',
    header: copy.components.sduDashboard.endpointTableColumns.lastSynced,
    // ISO 8601 timestamps sort correctly as strings; empty string keeps
    // never-synced rows at the bottom of an ascending sort.
    sortByAlpha: (item) => item.lastSynced ?? '',
    sortTestId: SDU_DASHBOARD_ENDPOINT_TABLE_COLUMN_SORT('lastSynced'),
  },
];

const searchFilter = (item: SduEndpointTableRow, query: string) => {
  const trimmed = query.trim().toLowerCase();
  return item.url.toLowerCase().includes(trimmed);
};

export const SduEndpointConsumptionTable: React.VFC<SduEndpointConsumptionTableProps> = ({
  onRowClick,
  rows,
}) => {
  const renderRow = (row: SduEndpointTableRow, idx: number) => {
    const testId = SDU_DASHBOARD_ENDPOINT_TABLE_ROW(row.endpointId, idx);
    const urlContent =
      row.canDrillDown && onRowClick ? (
        <Link onClick={() => onRowClick(row)} size="md" testId={testId}>
          {row.url}
        </Link>
      ) : (
        <Body size="md" testId={testId}>
          {row.url}
        </Body>
      );

    return (
      <>
        <td>{urlContent}</td>
        <td>
          <Body
            size="md"
            testId={SDU_DASHBOARD_ENDPOINT_TABLE_ROW_UNITS(row.endpointId, idx)}
          >
            {row.totalUnits === null
              ? copy.components.sduDashboard.noData
              : formatTotalUnits(row.totalUnits)}
          </Body>
        </td>
        <td>
          <Body
            size="md"
            testId={SDU_DASHBOARD_ENDPOINT_TABLE_ROW_LAST_SYNCED(
              row.endpointId,
              idx
            )}
          >
            {formatLastSynced(row.lastSynced)}
          </Body>
        </td>
      </>
    );
  };

  return (
    <div className="organization-dashboard-card">
      <DataTable
        columns={COLUMNS}
        data={rows}
        getRowKey={(row) => row.endpointId}
        renderRow={renderRow}
        searchFilter={searchFilter}
        searchInputTestId={SDU_DASHBOARD_ENDPOINT_TABLE_SEARCH}
        searchPlaceholder={
          copy.components.sduDashboard.endpointTableSearchPlaceholder
        }
        title={copy.components.sduDashboard.endpointTableTitle}
      />
    </div>
  );
};
